import { Order, OrderStatus } from '@api/Orders/Order';
import { PaymentMethod, getOrderExpectedTotal, getOrderPaidTotal } from '@api/Orders/OrderPayments';

// ============================================================================
// ¿Este pedido se está cerrando SIN decir cómo se cobró?
//
// ## El hueco que tapa (decisión de Irving, 2026-09-11)
// El camino diseñado para una mesa es: "Cerrar mesa y pagar" consolida sus
// pedidos en UNA cuenta `WaitingForPayment` → el POS la cobra con su modal de
// pago → `completeOrder`. Ahí el cobro sí se pide.
//
// **Pero `completeOrder` nunca exigió medio de pago**, y hay dos botones que lo
// llaman sin pasar por el POS: "Completar pedido" del detalle de la lista y la
// acción de la tarjeta del tablero. Por ahí salían **~1 a 3 pedidos al día
// cerrados sin registro de cobro** — y eso no es un dato que falte: el dinero
// entró al cajón y el corte de caja no lo sabe. En agosto se tuvieron que
// reparar **47 mesas ($18,246.60) a mano** por esto.
//
// ## La regla, y por qué NO pregunta si es mesa
// Pregunta por el DINERO, no por el canal: *"¿queda algo por cobrar?"*. Así
// cubre también al pedido de la web que alguien marcó pagado sin decir con qué,
// y no molesta a los que ya están cobrados (el de Stripe trae su `payments[]`,
// el del POS se cobró al crearse). Un pedido cubierto por completo con puntos
// no debe nada y no pregunta.
//
// ⚠️ **No es un candado**: quien cierra puede omitir el registro (Irving:
// *"que no sea bloqueante, que se pueda omitir"*). Lo que NO puede es cerrarlo
// sin enterarse — y omitir deja su propio renglón en la bitácora.
// ============================================================================

/** Tolerancia de un centavo: los porcentajes de promoción producen fracciones. */
const CENTAVO = 0.01;

/** Lo que falta por cobrar. `0` cuando ya está cubierto (nunca negativo). */
export const getPendingToCollect = (order?: Order): number => {
    if (!order) return 0;
    const falta = getOrderExpectedTotal(order) - getOrderPaidTotal(order);
    return falta > CENTAVO ? falta : 0;
};

/**
 * ¿Hay que pedir el cobro antes de completar este pedido?
 *
 * ⚠️ **Un pedido de $0 es legítimo en Krispis** (cortesías, promociones: hay 4
 * en producción, verificadas como reales). No se le puede pedir un cobro de
 * nada, así que nunca pregunta.
 */
export const needsPaymentBeforeComplete = (order?: Order): boolean => {
    if (!order) return false;
    if (getOrderExpectedTotal(order) <= CENTAVO) return false;
    return getPendingToCollect(order) > 0;
};

/**
 * Cómo se nombra cada medio de pago en español. Vive aquí —y no en el módulo de
 * paridad `OrderPayments`— porque es texto, y ese módulo tiene que seguir siendo
 * réplica exacta con krispis-web, donde estos textos no se usan.
 *
 * Lo leen el modal del cobro y **el renglón de la bitácora**, así que cambiar una
 * palabra cambia lo que queda escrito en el historial de un pedido.
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    [PaymentMethod.Cash]: 'efectivo',
    [PaymentMethod.CreditCard]: 'tarjeta',
    [PaymentMethod.BankTransfer]: 'transferencia'
};

// ============================================================================
// Los estados que CIERRAN un pedido no se escriben a mano.
//
// El quinto camino (2026-09-29): el selector de estado de `ModalViewOrder`
// guardaba `Completed` con `updateOrder`, la escritura plana — sin puntos, sin
// cash-flow del efectivo, sin bono de bebidas, sin Wallet y sin liberar la mesa.
// `Cancelled` por la misma vía tampoco devolvía el inventario ni descontaba las
// bebidas. Y salir de uno de los dos a mano deja esos efectos a medias (las
// bebidas contadas, el inventario ya devuelto).
//
// Cerrar es de `completeOrder` (detrás del gate del cobro) y cancelar es de
// `cancelOrder`. Por eso el candado vive en `updateOrder`, la FUNCIÓN, y no en el
// selector: el día que aparezca otra pantalla que la use, ya está tapada.
// ============================================================================

export const CLOSING_STATUSES: OrderStatus[] = [OrderStatus.Completed, OrderStatus.Cancelled];

export const isClosedStatus = (status?: OrderStatus): boolean => !!status && CLOSING_STATUSES.includes(status);

/**
 * ¿Esta escritura plana mete o saca al pedido de un estado que lo cierra? Devuelve
 * el motivo en español, o `undefined` si se puede guardar. Sin `previous` no se
 * sabe de dónde viene: un pedido que se escribe cerrado se rechaza.
 */
export const closingStatusChangeError = (next: Order, previous?: Order | null): string | undefined => {
    const antes = previous?.status;
    const despues = next.status;
    if (antes === despues) return undefined;
    if (despues === OrderStatus.Completed) {
        return 'Un pedido se completa con el botón "Completar", que otorga los puntos y registra el cobro';
    }
    if (despues === OrderStatus.Cancelled) {
        return 'Un pedido se cancela con el botón "Cancelar", que devuelve el inventario y el bono de bebidas';
    }
    if (isClosedStatus(antes)) {
        return 'Un pedido completado o cancelado ya no cambia de estado a mano';
    }
    return undefined;
};
